<template>
    <div class="dateRange-wrapper" :class="{ 'has-sliders': showSliders }">
        <button class="button is-cancel is-slider" @click.prevent="slideDates(true)" v-if="showSliders">
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
        </button>
        <div class="dateRangeFilter">
            <dateRangePicker
                ref="dateRangePicker"
                v-model="dateRange"
                :ranges="ranges"
                :locale-data="localeData"
                :min-date="minDate"
                :max-date="maxDate"
                :linkedCalendars="true"
                :alwaysShowCalendars="true"
                :autoApply="true"
                :append-to-body="true"
                :showDropdowns="false"
                :opens="opens"
                :singleDatePicker="restriction === 'day'"
                @update="onUpdate"
                @select="onSelect">
                <template v-slot:footer :clickApply="clickedApply">
                    <div class="drp-buttons">
                        <a href="javascript:void(0);" class="button is-cancel" @click.prevent="resetDates">{{
                            $t('reset')
                        }}</a>
                        <a href="javascript:void(0);" class="button is-cancel ml-auto" @click.prevent="doCancel">{{
                            $t('cancel')
                        }}</a>
                    </div>
                </template>
            </dateRangePicker>
        </div>
        <button class="button is-cancel is-slider" @click.prevent="slideDates()" v-if="showSliders">
            <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </button>
    </div>
</template>
<script>
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

library.add(faChevronLeft);
library.add(faChevronRight);

export default {
    name: 'DateRangeFilter',
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        minDate: {
            type: String,
            default: null,
        },
        maxDate: {
            type: String,
            default: null,
        },
        showSliders: {
            type: Boolean,
            default: false,
        },
        opens: {
            type: String,
            default: 'right', //left, right, center, inline
        },
        restriction: {
            type: String,
            default: null, // 'day', 'week', 'month', 'year'
            validator: (value) => [null, 'day', 'week', 'month', 'year'].includes(value),
        },
    },
    components: { DateRangePicker, FontAwesomeIcon },
    data() {
        return {
            dateRange: {
                startDate: null,
                endDate: null,
            },
            localeData: {},
            ranges: {},
        };
    },
    watch: {
        value(newValue, oldValue) {
            this.handleValue();
        },
        restriction() {
            this.setRanges();
        },
    },
    created() {
        this.handleValue();
        this.setRanges();
    },
    methods: {
        handleValue() {
            // Omit the time in the date, but preserve local time zone for string-based dates
            if (Array.isArray(this.value) && this.value.length < 2) {
                this.dateRange = {
                    startDate: null,
                    endDate: null,
                };
            } else {
                var start = this.value ? this.value[0] : null;
                /*if (start && typeof start == 'object') {
                    this.dateRange.startDate = new Date(start);
                } else if (start && typeof start == 'string') {
                    const [year, month, day] = start.split('T')[0].split('-').map(Number);
                    this.dateRange.startDate = new Date(year, month - 1, day);
                } else {
                    this.dateRange.startDate = null;
                }*/

                var end = this.value ? this.value[1] : null;
                /*if (end && typeof end == 'object') {
                    this.dateRange.endDate = new Date(end);
                } else if (end && typeof end == 'string') {
                    const [year, month, day] = end.split('T')[0].split('-').map(Number);
                    this.dateRange.endDate = new Date(year, month - 1, day);
                } else {
                    this.dateRange.endDate = null;
                }*/

                this.dateRange.startDate = start ? new Date(start) : null;
                this.dateRange.endDate = end ? new Date(end) : null;
            }
        },
        onUpdate() {
            this.handleRestriction();
        },

        onSelect(dateRange) {
            // Handle immediate selection for single day picker
            if (this.restriction === 'day') {
                this.dateRange.startDate = dateRange.startDate;
                this.dateRange.endDate = dateRange.startDate; // Same day for single date
                this.emitDates();
            }
        },

        handleRestriction() {
            if (!this.restriction || !this.dateRange.startDate) {
                this.emitDates();
                return;
            }

            let adjustedEndDate = new Date(this.dateRange.startDate);

            switch (this.restriction) {
                case 'day':
                    // For single day, end date is same as start date
                    adjustedEndDate = new Date(this.dateRange.startDate);
                    break;

                case 'week':
                    // Set to end of week (6 days from start)
                    adjustedEndDate.setDate(adjustedEndDate.getDate() + 6);
                    break;

                case 'month':
                    // Set to last day of the same month
                    adjustedEndDate = new Date(adjustedEndDate.getFullYear(), adjustedEndDate.getMonth() + 1, 0);
                    break;

                case 'year':
                    // Set to last day of the same year
                    adjustedEndDate = new Date(adjustedEndDate.getFullYear(), 11, 31);
                    break;
            }

            this.dateRange.endDate = adjustedEndDate;
            this.emitDates();
        },

        emitDates() {
            var start = this.dateRange.startDate;
            // Preserve local time exactly at midnight
            const localStart = new Date(
                start.getFullYear(),
                start.getMonth(),
                start.getDate(),
                0,
                0,
                0,
                0
            ).toISOString();

            var end = this.dateRange.endDate;
            // Preserve local time exactly at end of day
            const localEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).toISOString();

            this.$emit('input', [localStart, localEnd]);
        },
        resetDates() {
            this.dateRange.startDate = null;
            this.dateRange.endDate = null;
            this.$emit('input', []);
            this.$refs.dateRangePicker.togglePicker(false, true);
        },
        doCancel() {
            this.$refs.dateRangePicker.togglePicker(false, true);
        },
        slideDates(next) {
            // Detect the type of range based on the current selection
            const rangeType = this.detectRangeType();

            let newStartDate, newEndDate;

            switch (rangeType) {
                case 'day':
                    // Single day or custom range - slide by the range duration
                    const diffInDays = this.calculateDaysDifference();
                    const slideAmount = diffInDays === 0 ? 1 : diffInDays;
                    const direction = next ? -slideAmount : slideAmount;

                    newStartDate = new Date(this.dateRange.startDate);
                    newEndDate = new Date(this.dateRange.endDate);
                    newStartDate.setDate(newStartDate.getDate() + direction);
                    newEndDate.setDate(newEndDate.getDate() + direction);
                    break;

                case 'week':
                    // Week range - slide by 1 week (7 days)
                    const weekDirection = next ? -7 : 7;
                    newStartDate = new Date(this.dateRange.startDate);
                    newEndDate = new Date(this.dateRange.endDate);
                    newStartDate.setDate(newStartDate.getDate() + weekDirection);
                    newEndDate.setDate(newEndDate.getDate() + weekDirection);
                    break;

                case 'month':
                    // Month range - slide by 1 month
                    const monthDirection = next ? -1 : 1;

                    // Get the current month and year
                    const currentYear = this.dateRange.startDate.getFullYear();
                    const currentMonth = this.dateRange.startDate.getMonth();

                    // Calculate new month and year
                    const newMonth = currentMonth + monthDirection;
                    const newYear = currentYear + Math.floor(newMonth / 12);
                    const normalizedMonth = ((newMonth % 12) + 12) % 12;

                    // Create new start date (first day of new month)
                    newStartDate = new Date(newYear, normalizedMonth, 1);

                    // Create new end date (last day of new month)
                    newEndDate = new Date(newYear, normalizedMonth + 1, 0);
                    break;

                case 'year':
                    // Year range - slide by 1 year
                    const yearDirection = next ? -1 : 1;
                    const currentYearForYear = this.dateRange.startDate.getFullYear();
                    const newYearForYear = currentYearForYear + yearDirection;

                    newStartDate = new Date(newYearForYear, 0, 1); // Jan 1
                    newEndDate = new Date(newYearForYear, 11, 31); // Dec 31
                    break;
            }

            this.dateRange.startDate = newStartDate;
            this.dateRange.endDate = newEndDate;
            this.onUpdate();
        },
        detectRangeType() {
            const start = new Date(this.dateRange.startDate);
            const end = new Date(this.dateRange.endDate);

            // Check if it's the same day
            if (this.isSameDay(start, end)) {
                return 'day';
            }

            // Check if it's a week range (6 day difference, typically Mon-Sun = 6 days)
            const daysDiff = this.calculateDaysDifference();
            if (daysDiff === 6) {
                return 'week';
            }

            // Check if it's a month range (starts on 1st and ends on last day of same month)
            if (
                this.isFirstDayOfMonth(start) &&
                this.isLastDayOfMonth(end) &&
                start.getMonth() === end.getMonth() &&
                start.getFullYear() === end.getFullYear()
            ) {
                return 'month';
            }

            // Check if it's a year range (Jan 1 to Dec 31 of same year)
            if (
                this.isFirstDayOfYear(start) &&
                this.isLastDayOfYear(end) &&
                start.getFullYear() === end.getFullYear()
            ) {
                return 'year';
            }

            // Default to day-based sliding for custom ranges
            return 'day';
        },
        calculateDaysDifference() {
            const startAtMidnight = new Date(
                this.dateRange.startDate.getFullYear(),
                this.dateRange.startDate.getMonth(),
                this.dateRange.startDate.getDate()
            );

            const endAtMidnight = new Date(
                this.dateRange.endDate.getFullYear(),
                this.dateRange.endDate.getMonth(),
                this.dateRange.endDate.getDate()
            );

            return Math.round((endAtMidnight.getTime() - startAtMidnight.getTime()) / (24 * 60 * 60 * 1000));
        },
        isSameDay(date1, date2) {
            return (
                date1.getFullYear() === date2.getFullYear() &&
                date1.getMonth() === date2.getMonth() &&
                date1.getDate() === date2.getDate()
            );
        },
        isFirstDayOfMonth(date) {
            return date.getDate() === 1;
        },
        isLastDayOfMonth(date) {
            const nextDay = new Date(date);
            nextDay.setDate(date.getDate() + 1);
            return nextDay.getDate() === 1;
        },
        isFirstDayOfYear(date) {
            return date.getMonth() === 0 && date.getDate() === 1;
        },
        isLastDayOfYear(date) {
            return date.getMonth() === 11 && date.getDate() === 31;
        },
        setRanges() {
            const year = new Date().getFullYear();
            const month = new Date().getMonth();
            const date = new Date().getDate();
            const day = new Date().getDay();
            const weekStartDay = new Date().getDate() - new Date().getDay() + (new Date().getDay() === 0 ? -6 : 1);
            const weekLastDay = new Date().getDate() - (new Date().getDay() - 1) + 6;

            const midnight = new Date();
            const endDay = new Date();
            const yesterday = new Date(new Date().setDate(date - 1));
            const endYesterday = new Date(new Date().setDate(date - 1));

            const weekStart = new Date(new Date().setDate(weekStartDay));
            const weekEnd = new Date(new Date().setDate(weekLastDay));
            const previousWeekStart = new Date(new Date().setDate(weekStartDay - 7));
            const previousWeekEnd = new Date(new Date().setDate(date - (day - 1) - 1));

            const monthStart = new Date(year, month, 1);
            const monthEnd = new Date(year, month + 1, 0);
            const previousMonthStart = new Date(year, month - 1, 1);
            const previousMonthEnd = new Date(year, month, 0);

            const yearStart = new Date(year, 0, 1);
            const yearEnd = new Date(year + 1, 0, 0);
            const previousYearStart = new Date(year - 1, 0, 1);
            const previousYearEnd = new Date(year, 0, 0);

            this.ranges = {};

            // Filter ranges based on restriction
            if (!this.restriction || this.restriction === 'day') {
                this.ranges[this.$t('dateRange.dateRanges.Today')] = [midnight, endDay];
                this.ranges[this.$t('dateRange.dateRanges.Yesterday')] = [yesterday, endYesterday];
            }

            if (!this.restriction || this.restriction === 'week') {
                this.ranges[this.$t('dateRange.dateRanges.Thisweek')] = [weekStart, weekEnd];
                this.ranges[this.$t('dateRange.dateRanges.Previousweek')] = [previousWeekStart, previousWeekEnd];
            }

            if (!this.restriction || this.restriction === 'month') {
                this.ranges[this.$t('dateRange.dateRanges.Thismonth')] = [monthStart, monthEnd];
                this.ranges[this.$t('dateRange.dateRanges.Previousmonth')] = [previousMonthStart, previousMonthEnd];
            }

            if (!this.restriction || this.restriction === 'year') {
                this.ranges[this.$t('dateRange.dateRanges.Thisyear')] = [yearStart, yearEnd];
                this.ranges[this.$t('dateRange.dateRanges.Previousyear')] = [previousYearStart, previousYearEnd];
            }

            this.localeData = {
                separator: ' - ',
                format: 'dd/mm/yyyy',
                firstDay: 1,
                daysOfWeek: [
                    this.$t('dateRange.days.sun'),
                    this.$t('dateRange.days.mon'),
                    this.$t('dateRange.days.tue'),
                    this.$t('dateRange.days.wed'),
                    this.$t('dateRange.days.thu'),
                    this.$t('dateRange.days.fri'),
                    this.$t('dateRange.days.sat'),
                ],
                monthNames: [
                    this.$t('dateRange.months.jan'),
                    this.$t('dateRange.months.feb'),
                    this.$t('dateRange.months.mar'),
                    this.$t('dateRange.months.apr'),
                    this.$t('dateRange.months.may'),
                    this.$t('dateRange.months.jun'),
                    this.$t('dateRange.months.jul'),
                    this.$t('dateRange.months.aug'),
                    this.$t('dateRange.months.sep'),
                    this.$t('dateRange.months.oct'),
                    this.$t('dateRange.months.nov'),
                    this.$t('dateRange.months.dec'),
                ],
            };
        },
    },
    i18n: {
        messages: {
            en: {
                reset: 'Reset',
                cancel: 'Cancel',
                dateRange: {
                    months: {
                        jan: 'Jan',
                        feb: 'Feb',
                        mar: 'Mar',
                        apr: 'Apr',
                        may: 'May',
                        jun: 'Jun',
                        jul: 'Jul',
                        aug: 'Aug',
                        sep: 'Sep',
                        oct: 'Oct',
                        nov: 'Nov',
                        dec: 'Dec',
                    },
                    days: {
                        sun: 'Sun',
                        mon: 'Mon',
                        tue: 'Tue',
                        wed: 'Wed',
                        thu: 'Thu',
                        fri: 'Fri',
                        sat: 'Sat',
                    },
                    dateRanges: {
                        Today: 'Today',
                        Yesterday: 'Yesterday',
                        Thisweek: 'This week',
                        Previousweek: 'Previous week',
                        Thismonth: 'This month',
                        Previousmonth: 'Previous month',
                        Thisyear: 'This year',
                        Previousyear: 'Previous year',
                    },
                },
            },
            fr: {
                reset: 'Reset',
                cancel: 'Annulé',

                dateRange: {
                    months: {
                        jan: 'Janv',
                        feb: 'Févr',
                        mar: 'Mars',
                        apr: 'Avril',
                        may: 'Mai',
                        jun: 'Juin',
                        jul: 'Juil',
                        aug: 'Août',
                        sep: 'Sept',
                        oct: 'Oct',
                        nov: 'Nov',
                        dec: 'Déc',
                    },
                    days: {
                        sun: 'Dim',
                        mon: 'Lun',
                        tue: 'Mar',
                        wed: 'Mer',
                        thu: 'Jeu',
                        fri: 'Ven',
                        sat: 'Sam',
                    },
                    dateRanges: {
                        Today: "Aujourd'hui",
                        Yesterday: 'Hier',
                        Thisweek: 'Cette semaine',
                        Previousweek: 'Semaine précédente',
                        Thismonth: 'Ce mois',
                        Previousmonth: 'Mois précédent',
                        Thisyear: 'Cette année',
                        Previousyear: 'Année précédente',
                    },
                },
            },
            nl: {
                reset: 'Reset',
                cancel: 'Annuleren',
                dateRange: {
                    months: {
                        jan: 'Jan',
                        feb: 'Feb',
                        mar: 'Mrt',
                        apr: 'Apr',
                        may: 'Mei',
                        jun: 'Jun',
                        jul: 'Jul',
                        aug: 'Aug',
                        sep: 'Sep',
                        oct: 'Okt',
                        nov: 'Nov',
                        dec: 'Dec',
                    },
                    days: {
                        sun: 'Zo',
                        mon: 'Ma',
                        tue: 'Di',
                        wed: 'Wo',
                        thu: 'Do',
                        fri: 'Vr',
                        sat: 'Za',
                    },
                    dateRanges: {
                        Today: 'Vandaag',
                        Yesterday: 'Gisteren',
                        Thisweek: 'Deze week',
                        Previousweek: 'Vorige week',
                        Thismonth: 'Deze maand',
                        Previousmonth: 'Vorige maand',
                        Thisyear: 'Dit jaar',
                        Previousyear: 'Vorig jaar',
                    },
                },
            },
        },
    },
};
</script>

<style lang="scss">
/* Date Range Picker
========================================================== */
.vue-daterange-picker {
    width: 100%;
}

.dateRange-wrapper {
    display: flex;

    .dateRangeFilter {
        width: 100%;
        position: relative;

        svg {
            color: #ccc;
            position: absolute;
            top: 12px;
            z-index: 10;
            display: block;
            left: 12px;
        }
    }

    &.has-sliders {
        .form-control.reportrange-text {
            border-left: 0;
            border-right: 0;
        }
    }

    .glyphicon.glyphicon-calendar {
        display: none;
    }

    button.button.is-slider {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        font-size: 1.2rem;
        flex: 1 1 auto;
        height: auto;
        &:first-child {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
        &:last-child {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }
    }

    .form-control.reportrange-text {
        border: none;
        border-radius: 4px;
        border: 2px solid var(--background-color-darker);
        box-shadow: none;
        flex: 1 1 auto;
        height: 40px;
        display: flex;
        align-items: center;

        text-align: center;

        &:focus {
            border-color: color-mix(in srgb, var(--background-color-darker), #000 10%);
            box-shadow: none;
        }

        span {
            font-size: 1.4rem;
        }
    }
}

html body .daterangepicker {
    //position: fixed;
    z-index: 9999;
    .calendars {
        display: flex;
    }

    .drp-selected {
        display: none;
    }

    .ranges {
        ul {
            padding: 0 10px;

            li {
                &.active {
                    background-color: #3bc663;
                }

                &:focus {
                    outline: none;
                }
            }
        }
    }

    &.show-calendar .drp-buttons {
        display: flex;
        text-align: left;
    }

    .drp-buttons {
        .btn {
            margin: 0;
            font-size: 1rem;
            height: 2.25em;
            box-shadow: none;
            border: 1px solid transparent;
            padding-bottom: calc(0.375em - 1px);
            padding-left: 0.75em;
            padding-right: 0.75em;
            padding-top: calc(0.375em - 1px);
            text-align: center;
            white-space: nowrap;
            font-family: 'Inter', sans-serif;
            font-weight: 700;
            border-radius: 2px;
            letter-spacing: -0.02rem;
            cursor: pointer;

            &.btn-default {
                background-color: var(--background-color-darker);
                color: #888;
                border-color: var(--background-color-darker);
                margin-right: 10px;

                &:hover {
                    background-color: #e3e3e3;
                }
            }

            &.btn-success {
                &:hover {
                    background-color: var(--success);
                }
            }

            &:focus {
                outline: none;
            }
        }
    }

    select.monthselect {
        width: 28%;
    }

    .yearselect[data-v-6ca52014] {
        width: 55px;
    }

    td {
        &:hover {
            background-color: var(--success);
        }
    }

    td.in-range {
        background-color: rgba(59, 198, 99, 0.3);
    }

    td.today {
        background-color: var(--primary);
        color: #fff;
    }

    td.active {
        background-color: var(--success);

        &:hover {
            background-color: var(--success);
        }
    }
}
</style>
